import React from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';

export interface CheckboxOption {
  id: string;
  label: string;
}

export interface CheckboxGroupProps {
  title?: string;
  options: CheckboxOption[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  columns?: number;
  showBorder?: boolean;
  disabledOptions?: string[];
  getDisabledOption?: (option: CheckboxOption) => boolean;
  showSelectAll?: boolean;
  showOther?: boolean;
  onSelectAllChange?: (checked: boolean) => void;
  onOtherChange?: (checked: boolean) => void;
  isOtherChecked?: boolean;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  title,
  options,
  selectedIds,
  onChange,
  columns = 5,
  showBorder = false,
  disabledOptions = [],
  getDisabledOption,
  showSelectAll = false,
  showOther = false,
  onSelectAllChange,
  onOtherChange,
  isOtherChecked = false,
}) => {
  const handleSelect = (optionId: string) => {
    onChange([...selectedIds, optionId]);
  };

  const handleDeselect = (optionId: string) => {
    onChange(selectedIds.filter((id) => id !== optionId));
  };

  // Calculate if all non-disabled options are selected
  const enabledOptions = React.useMemo(
    () =>
      options.filter(
        (option) => !(getDisabledOption?.(option) ?? disabledOptions.includes(option.id))
      ),
    [options, disabledOptions, getDisabledOption]
  );

  const isAllSelected = React.useMemo(() => {
    return (
      enabledOptions.length > 0 && enabledOptions.every((option) => selectedIds.includes(option.id))
    );
  }, [enabledOptions, selectedIds]);

  const isSelectAllIndeterminate = React.useMemo(() => {
    if (enabledOptions.length === 0) return false;
    const anySelected = enabledOptions.some((option) => selectedIds.includes(option.id));
    return anySelected && !isAllSelected;
  }, [enabledOptions, selectedIds, isAllSelected]);

  const handleSelectAll = (checked: boolean) => {
    if (onSelectAllChange) {
      onSelectAllChange(checked);
    } else {
      // Default behavior: select/deselect all enabled options currently shown
      const enabledOptionIds = enabledOptions.map((option) => option.id);

      if (checked) {
        onChange([...new Set([...selectedIds, ...enabledOptionIds])]);
      } else {
        onChange(selectedIds.filter((id) => !enabledOptionIds.includes(id)));
      }
    }
  };

  const headerCheckboxes = (
    <FormGroup sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {showSelectAll && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSelectAllIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            }
            label='すべて'
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '16px',
                lineHeight: 1.6,
              },
            }}
          />
        )}
        {showOther && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isOtherChecked}
                onChange={(e) => onOtherChange?.(e.target.checked)}
              />
            }
            label='その他'
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '16px',
                lineHeight: 1.6,
              },
            }}
          />
        )}
      </Box>
    </FormGroup>
  );

  const content = (
    <FormGroup>
      {(showSelectAll || showOther) && headerCheckboxes}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: `repeat(${Math.min(columns, 3)}, 1fr)`,
            md: `repeat(${columns}, 1fr)`,
          },
          gap: 1.5,
        }}
      >
        {options.map((option) => {
          const isDisabled = getDisabledOption?.(option) ?? disabledOptions.includes(option.id);
          const isSelected = selectedIds.includes(option.id);
          return (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => {
                    if (isSelected) {
                      handleDeselect(option.id);
                    } else {
                      handleSelect(option.id);
                    }
                  }}
                  disabled={isDisabled}
                />
              }
              label={option.label}
              sx={{
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: isDisabled ? '#C4C6D0' : 'inherit',
                },
              }}
            />
          );
        })}
      </Box>
    </FormGroup>
  );

  return (
    <Box>
      {title && (
        <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      )}
      {showBorder ? (
        <Box
          sx={{
            border: '1px solid #E5E7EB',
            borderRadius: 1,
            p: 2,
            backgroundColor: '#FAFAFA',
          }}
        >
          {content}
        </Box>
      ) : (
        content
      )}
    </Box>
  );
};
